import type { ReactNode } from 'react';
import { Outlet } from 'react-router-dom';

import { NotFoundPage } from '@/routes/NotFoundPage';

import { hasRole, type Role } from './roles';
import { useMe } from './useMe';

/**
 * Route guard by role. A person outside `roles` gets the Not found page, the same as an
 * address that does not exist: the CRM does not advertise screens someone cannot use.
 *
 *   { path: 'payments', element: <RequireRole roles={ADMIN_ROLES} />, children: [...] }
 *   <RequireRole roles={ADMIN_ROLES}><RecordPaymentButton /></RequireRole>   (fallback null)
 *
 * Presentation only: a trainer is kept out of payments by RLS, not by this.
 */
export function RequireRole({
  roles,
  children,
  fallback,
}: {
  roles: readonly Role[];
  children?: ReactNode;
  /** Rendered for anyone else. Defaults to the Not found page for routes, nothing for elements. */
  fallback?: ReactNode;
}) {
  const { role } = useMe();
  if (!hasRole(role, roles)) return <>{fallback ?? (children ? null : <NotFoundPage />)}</>;
  return <>{children ?? <Outlet />}</>;
}
